import React, { useState, useEffect, useRef, useCallback } from 'react';
import api from '../services/api';

/**
 * VindexPanel — vehicle intelligence + "Get Buy Number", powered by our Vindex
 * valuation software (proxied through server/src/routes/vindex.js; spec lives
 * in vindex-market docs/lotsense-api.md).
 *
 * Flow (per the spec): snapshot on open → "Run it" if the VIN has never been
 * run → condition toggles initialized from the VHR prefill (accidents, frame,
 * branded title come pre-answered from the history report) → every toggle
 * change re-prices via exact-offer (debounced — each call is a real ACV
 * disclosure).
 */

const CONDITION_SCHEMA = [
  { key: 'accidents', label: 'Accidents', type: 'pick', options: ['0', '1', '2+'], danger: true },
  { key: 'title_present', label: 'Title in hand?', type: 'yesno', goodYes: true },
  { key: 'title_branded', label: 'Branded title?', type: 'yesno' },
  { key: 'frame_damage', label: 'Frame damage?', type: 'yesno' },
  { key: 'glass_damage', label: 'Glass damage?', type: 'yesno' },
  { key: 'rust_damage', label: 'Rust?', type: 'severity', options: ['Minor', 'Moderate', 'Major'] },
  { key: 'body_damage', label: 'Body damage?', type: 'severity', options: ['Minor', 'Moderate', 'Major'] },
  { key: 'engine_issues', label: 'Engine issues?', type: 'list', options: ['Engine Noise', 'Excessive Smoke', 'Runs Rough', 'Does Not Start', "Won't Stay Running"] },
  { key: 'mechanical_issues', label: 'Mechanical issues?', type: 'list', options: ['AC', 'Head Gasket', 'Oil Leak', 'Suspension', 'Steering'] },
  { key: 'warning_lights', label: 'Warning lights?', type: 'list', options: ['Airbag', 'Battery', 'Brakes', 'Engine', 'Pressure', 'Traction'] },
  { key: 'interior_issues', label: 'Interior issues?', type: 'list', options: ['Airbag', 'Electronics', 'Odor', 'Seat Damage', 'Other Damage'] },
  { key: 'modifications', label: 'Modifications?', type: 'list', options: ['Exhaust', 'Performance', 'Radio', 'Suspension', 'Wheels'] },
  { key: 'tire_issues', label: 'Tire issues?', type: 'list', options: ['Damaged', 'Incorrect Size', 'Mismatched', 'Missing Spare', 'Uneven Wear'] },
  { key: 'tire_tread', label: 'Tire tread', type: 'pick', options: ['Poor', 'Fair', 'Good'] },
];

const money = (n) => (n == null ? '—' : '$' + Number(n).toLocaleString());

const defaultConditions = () => ({
  accidents: '0', tire_tread: 'Good', title_present: true,
  title_branded: false, frame_damage: false, glass_damage: false,
  rust_damage: false, body_damage: false, engine_issues: false,
  mechanical_issues: false, warning_lights: false, interior_issues: false,
  modifications: false, tire_issues: false,
});

// Prefill values arrive in mixed shapes (["1"], true, "Minor", [..]) — normalize
// into our per-type state: pick=string, yesno=bool, severity=false|string, list=false|array
const applyPrefill = (byKey) => {
  const state = defaultConditions();
  if (!byKey) return state;
  CONDITION_SCHEMA.forEach(({ key, type }) => {
    const raw = byKey[key];
    if (raw === undefined || raw === null) return;
    if (type === 'pick') state[key] = String(Array.isArray(raw) ? raw[0] : raw);
    else if (type === 'yesno') state[key] = !!raw;
    else if (type === 'severity') state[key] = raw === false ? false : (raw === true ? 'Minor' : String(Array.isArray(raw) ? raw[0] : raw));
    else if (type === 'list') state[key] = raw === false ? false : (raw === true ? [] : (Array.isArray(raw) ? raw : [String(raw)]));
  });
  return state;
};

const VHR_STATUS_COLORS = {
  clean: 'bg-green-100 text-green-700',
  minor: 'bg-yellow-100 text-yellow-700',
  damage: 'bg-orange-100 text-orange-700',
  major: 'bg-red-100 text-red-700',
  branded: 'bg-red-600 text-white',
};

const VindexPanel = ({ vehicle = null, onClose }) => {
  const [vin, setVin] = useState(vehicle?.vin || '');
  const [miles, setMiles] = useState(vehicle?.mileage ? String(vehicle.mileage) : '');
  const [phase, setPhase] = useState(vehicle?.vin ? 'loading' : 'enter_vin'); // enter_vin | loading | no_run | ready | error
  const [snapshot, setSnapshot] = useState(null);
  const [loadError, setLoadError] = useState(null);

  const [rerun, setRerun] = useState(null); // { pieces, error }
  const [conditions, setConditions] = useState(defaultConditions());
  const [offer, setOffer] = useState(null);
  const [baseOffer, setBaseOffer] = useState(null); // as-sits (prefill) number — deltas measure against this
  const [offerLoading, setOfferLoading] = useState(false);
  const [offerError, setOfferError] = useState(null);
  const [appraisalOpen, setAppraisalOpen] = useState(false);

  const aliveRef = useRef(true);
  const debounceRef = useRef(null);
  const lastSheetRef = useRef(null);
  useEffect(() => () => { aliveRef.current = false; clearTimeout(debounceRef.current); }, []);

  const ingestSnapshot = useCallback((snap) => {
    setSnapshot(snap);
    setConditions(applyPrefill(snap?.exact_offer?.prefill?.by_key));
    if (snap?.vehicle?.mileage && !miles) setMiles(String(snap.vehicle.mileage));
    setOffer(null);
    setBaseOffer(null);
    lastSheetRef.current = null;
    setPhase('ready');
    // eslint-disable-next-line
  }, [miles]);

  const loadSnapshot = useCallback(async (v) => {
    setPhase('loading');
    setLoadError(null);
    try {
      const snap = await api.getVindexSnapshot(v);
      if (!aliveRef.current) return;
      ingestSnapshot(snap);
    } catch (err) {
      if (!aliveRef.current) return;
      if (err.status === 404) setPhase('no_run');
      else { setLoadError(err.message); setPhase('error'); }
    }
  }, [ingestSnapshot]);

  useEffect(() => {
    if (vehicle?.vin) loadSnapshot(vehicle.vin);
    // eslint-disable-next-line
  }, []);

  // ---- re-run (checkpointed job — poll until done) ----
  const startRerun = async (force = false) => {
    const v = vin.trim().toUpperCase();
    const m = parseInt(miles, 10) || undefined;
    setRerun({ pieces: null, error: null });
    let first = true;
    const poll = async () => {
      if (!aliveRef.current) return;
      try {
        const resp = await api.vindexRerun(v, m, force && first);
        first = false;
        if (!aliveRef.current) return;
        if (resp.status === 'done') {
          setRerun(null);
          ingestSnapshot(resp.snapshot);
        } else if (resp.status === 'error') {
          setRerun({ pieces: resp.progress?.pieces || null, error: 'Vindex could not complete the run' });
        } else {
          setRerun({ pieces: resp.progress?.pieces || null, error: null });
          setTimeout(poll, resp.poll_after_ms || 3000);
        }
      } catch (err) {
        if (!aliveRef.current) return;
        setRerun({ pieces: null, error: err.message });
      }
    };
    poll();
  };

  // ---- exact offer ----
  const buildSheet = useCallback(() => {
    const sheet = {};
    CONDITION_SCHEMA.forEach(({ key, type }) => {
      const val = conditions[key];
      if (type === 'list') sheet[key] = val === false ? false : (val.length ? val : true);
      else sheet[key] = val;
    });
    return sheet;
  }, [conditions]);

  const priceIt = useCallback(async (sheet, isBase = false) => {
    setOfferLoading(true);
    setOfferError(null);
    try {
      const data = await api.vindexExactOffer(vin.trim().toUpperCase(), parseInt(miles, 10) || undefined, sheet);
      if (!aliveRef.current) return;
      if (data.ok === false) throw new Error(data.error || 'No price returned');
      setOffer(data);
      if (isBase) setBaseOffer(data);
    } catch (err) {
      if (aliveRef.current) setOfferError(err.message);
    } finally {
      if (aliveRef.current) setOfferLoading(false);
    }
  }, [vin, miles]);

  const openAppraisal = () => {
    setAppraisalOpen(true);
    const sheet = buildSheet();
    lastSheetRef.current = JSON.stringify(sheet);
    priceIt(sheet, true); // first pull = the honest as-sits number
  };

  // Re-price on toggle changes (debounced — every call is a real ACV disclosure)
  useEffect(() => {
    if (!appraisalOpen || phase !== 'ready') return;
    const sheet = buildSheet();
    const sig = JSON.stringify(sheet);
    if (sig === lastSheetRef.current) return;
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      lastSheetRef.current = sig;
      priceIt(sheet, !baseOffer);
    }, 800);
    return () => clearTimeout(debounceRef.current);
    // eslint-disable-next-line
  }, [conditions]);

  const setCond = (key, value) => setConditions(c => ({ ...c, [key]: value }));
  const toggleListItem = (key, opt) => setConditions(c => {
    const cur = c[key];
    if (cur === false) return { ...c, [key]: [opt] };
    return { ...c, [key]: cur.includes(opt) ? cur.filter(x => x !== opt) : [...cur, opt] };
  });

  const delta = offer && baseOffer && offer !== baseOffer ? offer.price - baseOffer.price : null;

  const v = snapshot?.valuation;
  const vhr = snapshot?.vhr;
  const run = snapshot?.run;
  const veh = snapshot?.vehicle;

  const Pill = ({ active, danger, children, onClick }) => (
    <button
      onClick={onClick}
      className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${
        active
          ? (danger ? 'bg-red-600 border-red-600 text-white' : 'bg-blue-600 border-blue-600 text-white')
          : 'bg-white border-neutral-300 text-neutral-600 hover:border-neutral-400'
      }`}
    >
      {children}
    </button>
  );

  const renderCondition = (q) => {
    const val = conditions[q.key];
    if (q.type === 'pick') {
      return (
        <div key={q.key} className="py-1.5 border-b border-neutral-100 last:border-0">
          <div className="flex items-center justify-between gap-2">
            <span className="text-sm text-neutral-700">{q.label}</span>
            <div className="flex gap-1">
              {q.options.map(opt => (
                <Pill key={opt} active={val === opt} danger={q.danger && opt !== '0'} onClick={() => setCond(q.key, opt)}>{opt}</Pill>
              ))}
            </div>
          </div>
        </div>
      );
    }
    if (q.type === 'yesno') {
      return (
        <div key={q.key} className="py-1.5 border-b border-neutral-100 last:border-0">
          <div className="flex items-center justify-between gap-2">
            <span className="text-sm text-neutral-700">{q.label}</span>
            <div className="flex gap-1">
              <Pill active={val === true} danger={!q.goodYes} onClick={() => setCond(q.key, true)}>Yes</Pill>
              <Pill active={val === false} danger={q.goodYes} onClick={() => setCond(q.key, false)}>No</Pill>
            </div>
          </div>
        </div>
      );
    }
    if (q.type === 'severity') {
      return (
        <div key={q.key} className="py-1.5 border-b border-neutral-100 last:border-0">
          <div className="flex items-center justify-between gap-2">
            <span className="text-sm text-neutral-700">{q.label}</span>
            <div className="flex gap-1">
              <Pill active={val === false} onClick={() => setCond(q.key, false)}>No</Pill>
              {q.options.map(opt => (
                <Pill key={opt} active={val === opt} danger onClick={() => setCond(q.key, opt)}>{opt}</Pill>
              ))}
            </div>
          </div>
        </div>
      );
    }
    // list
    const on = val !== false;
    return (
      <div key={q.key} className="py-1.5 border-b border-neutral-100 last:border-0">
        <div className="flex items-center justify-between gap-2">
          <span className="text-sm text-neutral-700">{q.label}</span>
          <div className="flex gap-1">
            <Pill active={on} danger onClick={() => setCond(q.key, on ? val : [])}>Yes</Pill>
            <Pill active={!on} onClick={() => setCond(q.key, false)}>No</Pill>
          </div>
        </div>
        {on && (
          <div className="flex flex-wrap gap-1 mt-1.5 ml-2">
            {q.options.map(opt => (
              <Pill key={opt} active={val.includes(opt)} danger onClick={() => toggleListItem(q.key, opt)}>{opt}</Pill>
            ))}
          </div>
        )}
      </div>
    );
  };

  const pieceDot = (state) => state === 'done' || state === 'reused' ? '🟢' : state === 'pending' ? '🟡' : '🔴';

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[70] p-4" onClick={onClose}>
      <div className="bg-white w-full max-w-4xl rounded-xl shadow-2xl flex flex-col" style={{ maxHeight: '94vh' }} onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="px-5 py-3.5 border-b flex items-start justify-between flex-shrink-0">
          <div>
            <h2 className="font-semibold">
              Vindex — {veh ? `${veh.year} ${veh.make} ${veh.model}${veh.trim ? ' ' + veh.trim : ''}` : (vehicle ? `${vehicle.year} ${vehicle.make} ${vehicle.model}` : 'Vehicle Intelligence')}
            </h2>
            {run && (
              <p className="text-xs text-neutral-400">
                Last run {run.age_days < 1 ? 'today' : `${Math.round(run.age_days)} day${Math.round(run.age_days) === 1 ? '' : 's'} ago`} via {run.source}
                {vhr?.pulled_at && <> · history as of {new Date(vhr.pulled_at).toLocaleDateString()}</>}
              </p>
            )}
          </div>
          <div className="flex items-center gap-2">
            {phase === 'ready' && !rerun && (
              <button onClick={() => startRerun(true)} className="px-3 py-1.5 text-xs border border-neutral-300 rounded-lg hover:bg-neutral-50">
                ↻ Refresh Vindex data
              </button>
            )}
            <button onClick={onClose} className="text-neutral-400 hover:text-neutral-600 text-xl leading-none px-1">✕</button>
          </div>
        </div>

        {/* Re-run progress bar */}
        {rerun && (
          <div className="px-5 py-2 bg-blue-50 border-b flex items-center gap-3 text-xs flex-shrink-0">
            {rerun.error ? (
              <span className="text-red-600">{rerun.error}</span>
            ) : (
              <>
                <span className="font-medium text-blue-700 animate-pulse">Running fresh lookup…</span>
                {rerun.pieces && Object.entries(rerun.pieces).map(([k, s]) => (
                  <span key={k} className="text-neutral-600">{pieceDot(s)} {k}</span>
                ))}
              </>
            )}
          </div>
        )}

        <div className="overflow-y-auto flex-1">
          {phase === 'enter_vin' && (
            <div className="p-8 max-w-sm mx-auto text-center">
              <p className="text-sm text-neutral-500 mb-3">Enter a VIN to pull Vindex intelligence — works for any car, even ones not in inventory.</p>
              <input
                type="text" value={vin} onChange={e => setVin(e.target.value.toUpperCase())} maxLength={17}
                placeholder="17-character VIN" className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm font-mono mb-2"
              />
              <input
                type="number" value={miles} onChange={e => setMiles(e.target.value)}
                placeholder="Miles (needed if never run)" className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm mb-3"
              />
              <button
                onClick={() => loadSnapshot(vin.trim().toUpperCase())}
                disabled={vin.trim().length !== 17}
                className="w-full py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 text-sm font-medium"
              >
                Look up
              </button>
            </div>
          )}

          {phase === 'loading' && <div className="p-10 text-center text-neutral-400">Loading Vindex data…</div>}

          {phase === 'error' && (
            <div className="p-10 text-center">
              <p className="text-red-500 text-sm mb-3">{loadError}</p>
              <button onClick={() => loadSnapshot(vin.trim().toUpperCase())} className="px-4 py-2 border rounded-lg text-sm hover:bg-neutral-50">Try again</button>
            </div>
          )}

          {phase === 'no_run' && !rerun && (
            <div className="p-8 max-w-sm mx-auto text-center">
              <p className="text-sm text-neutral-600 mb-1 font-medium">This VIN has never been run through Vindex.</p>
              <p className="text-xs text-neutral-400 mb-3">A fresh run takes 5–30 seconds and pulls books, ACV, history, and a salvage check.</p>
              <input
                type="number" value={miles} onChange={e => setMiles(e.target.value)}
                placeholder="Miles (required)" className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm mb-3"
              />
              <button
                onClick={() => startRerun(false)}
                disabled={!parseInt(miles, 10)}
                className="w-full py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 text-sm font-medium"
              >
                ▶ Run it
              </button>
            </div>
          )}

          {phase === 'ready' && snapshot && (
            <div className="px-5 py-4 space-y-4">
              {/* Red banners: structural risk + salvage detection */}
              {vhr?.structural_risk?.detected && (
                <div className="rounded-lg bg-red-600 text-white px-4 py-2.5 text-sm font-semibold">
                  ⚠ STRUCTURAL RISK — {vhr.structural_risk.source_label || (vhr.structural_risk.sources || []).join(', ') || 'flagged by history scan'}
                </div>
              )}
              {snapshot.salvage?.detected && (
                <div className="rounded-lg bg-red-600 text-white px-4 py-2.5 text-sm font-semibold">
                  ⚠ SALVAGE AUCTION HISTORY — this VIN appeared at a salvage auction
                </div>
              )}

              {/* Signal pills + VHR summary */}
              <div className="flex flex-wrap items-center gap-2">
                {vhr && (
                  <>
                    <span className={`px-2.5 py-1 rounded-full text-xs font-bold ${VHR_STATUS_COLORS[vhr.status] || 'bg-neutral-100 text-neutral-600'}`}>
                      VHR {vhr.score ?? '—'} · {(vhr.status || '?').toUpperCase()}
                    </span>
                    {vhr.decision && <span className="px-2.5 py-1 rounded-full text-xs font-bold bg-neutral-800 text-white">{vhr.decision}</span>}
                    {(vhr.flags || []).map((f, i) => (
                      <span key={i} className="px-2 py-1 rounded-full text-[11px] bg-neutral-100 text-neutral-600 font-medium">{f}</span>
                    ))}
                  </>
                )}
                {(snapshot.signal_pills || []).filter(p => p && p.label).map((p, i) => (
                  <span key={i} className="px-2 py-1 rounded-full text-[11px] font-bold text-white" style={{ backgroundColor: p.color || '#6b7280' }}>{p.label}</span>
                ))}
              </div>

              {/* Books */}
              {v && (
                <div>
                  <p className="text-[11px] font-semibold text-neutral-400 uppercase tracking-wide mb-1.5">Wholesale books</p>
                  <div className="grid grid-cols-4 sm:grid-cols-8 gap-1.5">
                    {Object.entries(v.all_books || {}).map(([book, val]) => (
                      <div key={book} className={`rounded-lg border px-2 py-1.5 text-center ${book === v.lowest_book_source ? 'border-amber-400 bg-amber-50' : 'border-neutral-200'}`}>
                        <div className="text-[10px] text-neutral-400 font-semibold">{book}</div>
                        <div className="text-xs font-bold">{money(val)}</div>
                      </div>
                    ))}
                  </div>
                  <div className="flex flex-wrap gap-x-5 gap-y-1 mt-2 text-xs text-neutral-600">
                    <span>ACV band: <strong>{money(v.acv_low)} – {money(v.acv_est)} – {money(v.acv_high)}</strong></span>
                    {snapshot.vinsight && (
                      <span>VINsight bids: ultra-safe <strong>{money(snapshot.vinsight.ultra_safe)}</strong> · target <strong>{money(snapshot.vinsight.target)}</strong> · expected sale <strong>{money(snapshot.vinsight.expected_sale)}</strong></span>
                    )}
                  </div>
                </div>
              )}

              {/* Lane info when the car is on an upcoming auction run list */}
              {snapshot.lane && (
                <div className="rounded-lg border border-indigo-200 bg-indigo-50 px-3 py-2 text-xs text-indigo-800">
                  🏁 {snapshot.lane.auction_name} · {snapshot.lane.sale_date} · Lane {snapshot.lane.lane} Run {snapshot.lane.run_number}
                  {snapshot.lane.lights && <> · {snapshot.lane.lights} light</>}
                  {snapshot.lane.grade != null && <> · Grade {snapshot.lane.grade}</>}
                  {snapshot.lane.announcements && <> · "{snapshot.lane.announcements}"</>}
                </div>
              )}

              {/* Appraisal — exact offer */}
              <div className="border-t pt-3">
                {!appraisalOpen ? (
                  <button
                    onClick={openAppraisal}
                    className="w-full py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm font-semibold"
                  >
                    💰 Get Buy Number (prices as-sits from the history report)
                  </button>
                ) : (
                  <div className="flex gap-4 flex-col sm:flex-row">
                    {/* Conditions */}
                    <div className="flex-1 min-w-0">
                      <p className="text-[11px] font-semibold text-neutral-400 uppercase tracking-wide mb-1">
                        Condition sheet <span className="normal-case font-normal">— pre-answered from the VHR, change anything</span>
                      </p>
                      {CONDITION_SCHEMA.map(renderCondition)}
                    </div>
                    {/* Price */}
                    <div className="w-full sm:w-56 flex-shrink-0">
                      <div className="sm:sticky sm:top-0 bg-neutral-50 border rounded-xl px-4 py-3">
                        <p className="text-[11px] font-semibold text-neutral-400 uppercase tracking-wide">Buy Number</p>
                        {offerLoading ? (
                          <div className="text-3xl font-bold text-neutral-300 animate-pulse my-1">…</div>
                        ) : offer ? (
                          <div className="text-3xl font-bold text-green-700 my-1">{money(offer.price)}</div>
                        ) : (
                          <div className="text-3xl font-bold text-neutral-300 my-1">—</div>
                        )}
                        {delta != null && delta !== 0 && !offerLoading && (
                          <div className={`text-xs font-semibold mb-1 ${delta < 0 ? 'text-red-600' : 'text-green-600'}`}>
                            {delta < 0 ? '▼' : '▲'} {money(Math.abs(delta))} vs as-sits
                          </div>
                        )}
                        {offerError && <p className="text-xs text-red-500 mb-1">{offerError}</p>}
                        {offer && (
                          <div className="space-y-0.5 text-sm mt-2">
                            <div className="flex justify-between"><span className="text-neutral-500">Clean</span><span className="font-medium">{money(offer.clean_price)}</span></div>
                            <div className="flex justify-between"><span className="text-neutral-500">Average</span><span className="font-medium">{money(offer.average_price)}</span></div>
                            <div className="flex justify-between"><span className="text-neutral-500">Rough</span><span className="font-medium">{money(offer.rough_price)}</span></div>
                          </div>
                        )}
                        {baseOffer && (
                          <p className="text-[10px] text-neutral-400 mt-2">As-sits: {money(baseOffer.price)}</p>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-2.5 border-t flex items-center justify-between flex-shrink-0">
          <span className="text-[11px] text-neutral-400">Powered by Vindex · every price is a live ACV disclosure</span>
          <button onClick={onClose} className="px-4 py-1.5 text-sm text-neutral-500 hover:text-neutral-700">Close</button>
        </div>
      </div>
    </div>
  );
};

export default VindexPanel;
